import React, { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Alert, Button, Card, Descriptions, Spin, Typography, message } from 'antd'
import OtpAuthFlow from '../../components/OtpAuthFlow'
import { getOffPlatformOrder, claimOffPlatformOrder } from '../../api/captainOffPlatform'

function formatPickup(raw) {
  if (!raw) return null
  const v = String(raw).trim()
  if (/^\d{4}$/.test(v)) return `${v.slice(0, 2)}:${v.slice(2)}`
  return v || null
}

export default function CaptainOffPlatformOrderClaim() {
  const { id } = useParams()
  const orderId = Number(id)
  const [searchParams] = useSearchParams()
  const pickupTime = formatPickup(searchParams.get('pickup_at'))
  const [loading, setLoading] = useState(true)
  const [state, setState] = useState(null)
  const [data, setData] = useState(null)
  const [claiming, setClaiming] = useState(false)
  const otpRef = useRef(null)

  async function load() {
    setLoading(true)
    try {
      const r = await getOffPlatformOrder(orderId)
      if (r.status === 401) {
        otpRef.current?.open()
        setState(null)
        return
      }
      if (r.status === 403) { setState('not_a_captain'); return }
      if (r.status === 404) { setState('not_found'); return }
      setState(r.body.state)
      setData(r.body)
    } catch (e) {
      console.error(e)
      message.error('تعذر تحميل الطلب الخارجي')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [orderId])

  async function onClaim() {
    setClaiming(true)
    try {
      const r = await claimOffPlatformOrder(orderId)
      if (r.status === 409) {
        message.error('تم استلام الطلب من قبل كابتن آخر')
        await load()
        return
      }
      setState(r.body.state)
      setData(r.body)
    } catch (e) {
      console.error(e)
      message.error('تعذر استلام الطلب')
    } finally {
      setClaiming(false)
    }
  }

  return (
    <div style={{ maxWidth: 720, margin: '24px auto', padding: '0 12px' }} dir="rtl">
      <OtpAuthFlow ref={otpRef} onSuccess={() => load()} />

      {pickupTime && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={`وقت الاستلام: ${pickupTime}`} />
      )}

      {loading && <Spin />}

      {!loading && state === 'not_a_captain' && (
        <Alert type="error" showIcon message="أنت لست كابتن" description="هذه الصفحة مخصصة للكباتن فقط." />
      )}

      {!loading && state === 'not_found' && (
        <Alert type="error" showIcon message="الطلب غير موجود" />
      )}

      {!loading && state === 'cancelled' && (
        <Alert type="warning" showIcon message="الطلب ملغي" />
      )}

      {!loading && state === 'claimed_by_other' && (
        <Alert type="warning" showIcon message="الطلب مستلم" description="تم استلام هذا الطلب من قبل كابتن آخر." />
      )}

      {!loading && state === 'unclaimed' && data && (
        <Card title={`طلب خارجي #${orderId}`}>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="المتجر">{data.shopName || '-'}</Descriptions.Item>
            <Descriptions.Item label="منطقة التوصيل">{data.deliveryLocationName || '-'}</Descriptions.Item>
            <Descriptions.Item label="رسوم التوصيل">{data.deliveryFee || '-'}</Descriptions.Item>
            {data.notes && <Descriptions.Item label="ملاحظات">{data.notes}</Descriptions.Item>}
          </Descriptions>
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Button type="primary" size="large" loading={claiming} onClick={onClaim}>
              استلم الطلب
            </Button>
          </div>
        </Card>
      )}

      {!loading && state === 'claimed_by_me' && data && (
        <Card title={`طلب خارجي #${orderId} — تم الاستلام`}>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="المتجر">{data.shopName || '-'}</Descriptions.Item>
            <Descriptions.Item label="منطقة التوصيل">{data.deliveryLocationName || '-'}</Descriptions.Item>
            <Descriptions.Item label="رسوم التوصيل">{data.deliveryFee || '-'}</Descriptions.Item>
            {data.notes && <Descriptions.Item label="ملاحظات">{data.notes}</Descriptions.Item>}
            {data.claimedAt && <Descriptions.Item label="تاريخ الاستلام">{new Date(data.claimedAt).toLocaleString('ar')}</Descriptions.Item>}
          </Descriptions>
          <div style={{ marginTop: 16, padding: 12, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
            <Typography.Text strong>هذا طلب خارجي — اذهب إلى المتجر، استلم الطلب، وقم بالتوصيل لمنطقة الزبون.</Typography.Text>
          </div>
        </Card>
      )}
    </div>
  )
}
